import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { createKindnessChallengeEntry } from '@/services/webApi';
import { kindnessChallengeFormSchema } from '@goodneighbor/api/routes/kindness-challenges/kindnessChallenge.schema';
import { analytics } from '@/services/analytics';

export type KindnessChallengeValues = z.infer<
  typeof kindnessChallengeFormSchema
>;

export type KindnessChallengeSuccessData = {
  firstName: string;
};

export function useKindnessChallengeFormKey() {
  const [formKey] = useState(0);
  const [successData, setSuccessData] =
    useState<KindnessChallengeSuccessData | null>(null);

  const handleSuccess = useCallback((data: KindnessChallengeSuccessData) => {
    setSuccessData(data);
  }, []);

  return { formKey, successData, handleSuccess };
}

export function useKindnessChallengeForm(
  onSuccess: (data: KindnessChallengeSuccessData) => void
) {
  const searchParams = useSearchParams();
  const slugParam = searchParams.get('id') ?? '';

  const form = useForm<KindnessChallengeValues>({
    resolver: zodResolver(kindnessChallengeFormSchema),
    defaultValues: {
      slug: slugParam || '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      socialHandle: '',
      socialPlatform: '' as 'instagram' | 'tiktok' | 'facebook',
      termsAccepted: undefined
    }
  });

  const utmParams = {
    utmSource: searchParams.get('utm_source') ?? undefined,
    utmMedium: searchParams.get('utm_medium') ?? undefined,
    utmCampaign: searchParams.get('utm_campaign') ?? undefined,
    utmTerm: searchParams.get('utm_term') ?? undefined,
    utmContent: searchParams.get('utm_content') ?? undefined
  };

  const mutation = useMutation({
    mutationFn: createKindnessChallengeEntry,
    onSuccess: () => {
      const firstName = form.getValues('firstName');
      onSuccess({ firstName });
    }
  });

  const onSubmit = (data: KindnessChallengeValues) => {
    // TODO: Include utmParams in the Rudderstack event that gets triggered
    // on successful entry submission.
    mutation.mutate({
      slug: data.slug,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      socialHandle: data.socialHandle,
      socialPlatform: data.socialPlatform,
      termsAccepted: data.termsAccepted,
      anonymousId: analytics.getAnonymousId()
    });
  };

  return {
    form,
    mutation,
    onSubmit,
    slugParam
  };
}
